/* eslint-disable no-param-reassign */
import {
  commonStartAngle,
  drawTrash,
  updateTicketEdgeLine
} from 'rf-ui/GameRenderer/rendererHelper';
import TWEEN, { Tween } from '@tweenjs/tween.js';
import { smoothSequenceEasingWithCutoffValue } from 'rf-ui/GameRenderer/customEasing';
import { Group } from 'two.js/src/group';
import type WheelOfFortuneRenderer from 'rf-ui/GameRenderer/WheelOfFortuneRenderer';
import type { DrawnTicket } from 'rf-ui/GameRenderer/WheelOfFortuneRenderer';
import Ticket from 'rf-ui/GameRenderer/entities/ticket';

export const shrinkTicket = function (
  this: WheelOfFortuneRenderer,
  shrinkProxy: { size: number; rotation: number },
  numTickets: number,
  ticket: DrawnTicket,
  parameters: { wheelRadius: number; textPos: number },
) {
  ticket.wedge.visible=false;

  const disappearingWedgeAngle = (shrinkProxy.size * Math.PI * 2.0) / numTickets;
  const activeWedgesAngle = ((Math.PI * 2.0) - disappearingWedgeAngle) / (numTickets - 1);

  let startAngle = commonStartAngle;
  // eslint-disable-next-line no-unused-vars,no-restricted-syntax
  for (const drawnElements of Object.values(this.drawnPlayerElements)) {
    // eslint-disable-next-line no-loop-func
    drawnElements.tickets.forEach(t => {
      const endAngle = startAngle + ((t === ticket) ? disappearingWedgeAngle : activeWedgesAngle);
      t.wedge.resize(startAngle, endAngle - startAngle);

      updateTicketEdgeLine(t.line, startAngle, parameters);

      startAngle = endAngle;
    });
  }
  this.curRotation = shrinkProxy.rotation % (Math.PI * 2.0);
  this.wheel!.rotation = shrinkProxy.rotation % (Math.PI * 2.0);
};

export const expandTicket = function (
  this: WheelOfFortuneRenderer,
  expandProxy: { size: number; rotation: number },
  numTickets: number,
  ticket: DrawnTicket,
  parameters: { wheelRadius: number; textPos: number },
) {
  const gapAngle = (expandProxy.size * Math.PI * 2.0) / (numTickets + 1);
  const activeWedgesAngle = ((Math.PI * 2.0) - gapAngle) / numTickets;

  let startAngle = commonStartAngle;
  for (const drawnElements of Object.values(this.drawnPlayerElements)) {
    drawnElements.tickets.forEach(t => {
      const endAngle = startAngle + activeWedgesAngle;
      t.wedge.resize(startAngle, endAngle - startAngle);

      updateTicketEdgeLine(t.line, startAngle, parameters);

      startAngle = endAngle;
      // Reserve an empty wedge after the selected ticket for the incoming ticket.
      startAngle = endAngle + ((t === ticket) ? gapAngle : 0);
    });
  }
  this.curRotation = expandProxy.rotation % (Math.PI * 2.0);
  this.wheel!.rotation = expandProxy.rotation % (Math.PI * 2.0);
};

export const tweenTicketToStar = function (
  this: WheelOfFortuneRenderer,
  wedgeGroup: Ticket,
  size: number
) {
  const animationTime = 1000;
  const to = {
    x: (25 - Math.random() * 50) + (Math.random() > 0.5
      ? -this.sceneWidth * 0.4
      : this.sceneWidth * 0.4),
    yUp: -this.sceneHeight / 2,
    yDown: -this.sceneHeight * 0.3,
    scale: 0,
    rotation: (Math.floor(1 + Math.random() * 3) * (Math.PI * 2))
      + (Math.PI / 2 - size)
      - (0.2 + Math.random() * 0.4)
  };

  const yDownTween = new Tween({
    y: to.yUp,
  }).to({ y: to.yDown }, animationTime * 0.5)
    .easing(TWEEN.Easing.Quadratic.In)
    .onUpdate((proxy: { y: number }) => {
      wedgeGroup.translation.y = proxy.y;
    });

  const yUpTween = new Tween({
    y: wedgeGroup.translation.y,
  }).to({ y: to.yUp }, animationTime * 0.5)
    .easing(TWEEN.Easing.Quadratic.Out)
    .onUpdate((proxy: { y: number }) => {
      wedgeGroup.translation.y = proxy.y;
    })
    .chain(yDownTween)
    .start();

  const xTween = new Tween({
    x: wedgeGroup.translation.x,
  }).to({ x: to.x }, animationTime)
    .onUpdate((proxy: { x: number }) => {
      wedgeGroup.translation.x = proxy.x;
    })
    .start();

  const scaleTween = new Tween({
    scale: 1,
  }).to({ scale: to.scale }, animationTime)
    .onUpdate((proxy: { scale: number }) => {
      wedgeGroup.scale = proxy.scale;
    })
    .easing(TWEEN.Easing.Quadratic.Out)
    .onComplete(() => {
      this.animateExplodingStar(wedgeGroup.translation.x, wedgeGroup.translation.y);
      wedgeGroup.destroy();
      wedgeGroup.remove();
    })
    .start();

  const rotTween = new Tween({
    rotation: 0,
  }).to({ rotation: to.rotation }, animationTime)
    .onUpdate((proxy: { rotation: number }) => {
      wedgeGroup.rotation = proxy.rotation;
    })
    .easing(TWEEN.Easing.Quadratic.Out)
    .start();

  this.tweens.add(scaleTween, rotTween, yDownTween, yUpTween, xTween);
};

export const tweenTicketToScreen = function (
  this: WheelOfFortuneRenderer,
  wedgeGroup: Ticket,
  size: number
) {
  const to = {
    x: (-this.sceneWidth / 2 + 300) + (0 * (this.sceneWidth - 600)),
    y: -100 + Math.random() * (200),
    scale: 3,
    rotation: (Math.floor(1 + Math.random() * 3) * (Math.PI * 2))
      + (Math.PI / 2 - size)
      - (0.2 + Math.random() * 0.4)
  };

  const outOfScreenTween = new Tween({
    y: to.y,
  }).to({ y: this.two.height + 200 }, 1000)
    .delay(200)
    .onUpdate((proxy: { y: number }) => {
      wedgeGroup.translation.y = proxy.y;
    })
    .onComplete(() => {
      wedgeGroup.destroy();
      wedgeGroup.remove();
    })
    .easing(TWEEN.Easing.Exponential.In);

  const toPosTween = new Tween({
    x: wedgeGroup.translation.x,
    y: wedgeGroup.translation.y,
  }).to({
    x: to.x,
    y: to.y
  }, 1000)
    .onUpdate((proxy: { x: number; y: number }) => {
      wedgeGroup.translation.x = proxy.x;
      wedgeGroup.translation.y = proxy.y;
    })
    .easing(TWEEN.Easing.Linear.InOut)
    .chain(outOfScreenTween)
    .start();

  const scaleTween = new Tween({
    scale: 1,
  }).to({ scale: to.scale }, 1000)
    .onUpdate((proxy: { scale: number }) => {
      wedgeGroup.scale = proxy.scale;
    })
    .easing(TWEEN.Easing.Quadratic.Out)
    .start();

  const rotTween = new Tween({
    rotation: 0,
  }).to({ rotation: to.rotation }, 1000)
    .onUpdate((proxy: { rotation: number }) => {
      wedgeGroup.rotation = proxy.rotation;
    })
    .easing(TWEEN.Easing.Quadratic.Out)
    .start();

  this.tweens.add(scaleTween, rotTween, toPosTween, outOfScreenTween);
};

export const tweenTicketToGround = function (
  this: WheelOfFortuneRenderer,
  wedgeGroup: Ticket,
  size: number,
  parameters: { wheelRadius: number; textPos: number },
) {
  const edgeSpace = this.sceneWidth;
  const to = {
    x: -((this.sceneWidth / 2) - edgeSpace)
      + (Math.random() * (this.sceneWidth - edgeSpace * 2)),
    yUp: wedgeGroup.translation.y - 100,
    yDown: wedgeGroup.translation.y + 520
      - Math.tan(size / 2) * (parameters.wheelRadius * 0.5),
    rotation: (Math.floor(1 + Math.random() * 2) * (Math.PI * 2)) + (Math.PI / 2 - size)
  };

  const yDownTween = new Tween({
    y: to.yUp,
  }).to({ y: to.yDown }, 600)
    .easing(TWEEN.Easing.Quadratic.In)
    .onUpdate((proxy: { y: number }) => {
      wedgeGroup.translation.y = proxy.y;
    });

  const yUpTween = new Tween({
    y: wedgeGroup.translation.y,
  }).to({ y: to.yUp }, 400)
    .easing(TWEEN.Easing.Quadratic.Out)
    .onUpdate((proxy: { y: number }) => {
      wedgeGroup.translation.y = proxy.y;
    })
    .chain(yDownTween)
    .start();

  const xTween = new Tween({
    x: wedgeGroup.translation.x,
  }).to({ x: to.x }, 1000)
    .onUpdate((proxy: { x: number }) => {
      wedgeGroup.translation.x = proxy.x;
    })
    .start();

  const rotTween = new Tween({
    rotation: 0,
  }).to({ rotation: to.rotation }, 1000)
    .onUpdate((proxy: { rotation: number }) => {
      wedgeGroup.rotation = proxy.rotation;
    })
    .easing(TWEEN.Easing.Quadratic.Out)
    .start();

  this.tweens.add(yUpTween, yDownTween, xTween, rotTween);
};

export const tweenTicketToTrashCan = function (
  this: WheelOfFortuneRenderer,
  wedgeGroup: Ticket,
  size: number,
  parameters: { wheelRadius: number; textPos: number },
) {
  const edgeSpace = this.sceneWidth;
  const to = {
    x: -this.sceneWidth/2,
    yUp: wedgeGroup.translation.y - 300,
    yDown: this.sceneHeight - 100,
    rotation: (Math.floor(1 + Math.random() * 2) * (Math.PI * 2)) - size
  };

  const yDownTween = new Tween({
    y: to.yUp,
  }).to({ y: to.yDown }, 600)
    .easing(TWEEN.Easing.Quadratic.In)
    .onUpdate((proxy: { y: number }) => {
      wedgeGroup.translation.y = proxy.y;
    })
    .onComplete(() => {
      wedgeGroup.destroy();
      wedgeGroup.remove();
    });

  const yUpTween = new Tween({
    y: wedgeGroup.translation.y,
  }).to({ y: to.yUp }, 400)
    .easing(TWEEN.Easing.Quadratic.Out)
    .onStart(() => tweenTrashCanCatch.call(this))
    .onUpdate((proxy: { y: number }) => {
      wedgeGroup.translation.y = proxy.y;
    })
    .chain(yDownTween)
    .start();

  const xTween = new Tween({
    x: wedgeGroup.translation.x,
  }).to({ x: to.x }, 1000)
    .onUpdate((proxy: { x: number }) => {
      wedgeGroup.translation.x = proxy.x;
    })
    .start();

  const rotTween = new Tween({
    rotation: 0,
  }).to({ rotation: to.rotation }, 1000)
    .onUpdate((proxy: { rotation: number }) => {
      wedgeGroup.rotation = proxy.rotation;
    })
    .easing(TWEEN.Easing.Quadratic.Out)
    .start();

  this.tweens.add(yUpTween, yDownTween, xTween, rotTween);
};

export const tweenTrashCanCatch = function (
  this: WheelOfFortuneRenderer
) {
  const offscreenY = 500;

  const trash = drawTrash(this.two)
  this.scene?.add(trash)
  trash.translation.set(-this.sceneWidth/2, offscreenY);
  trash.rotation = 0.2;
  trash.opacity = 1;



  const yDownTween = new Tween({
    y: 300,
  }).to({ y: offscreenY }, 500)
    .delay(400)
    .easing(TWEEN.Easing.Quadratic.In)
    .onUpdate((proxy: { y: number }) => {
      trash.translation.y = proxy.y;
    })
    .onComplete(() => {
      trash.visible=false;
      trash.remove();
    });

  const yUpTween = new Tween({
    y: offscreenY,
  }).to({ y: 300 }, 500)
    .easing(TWEEN.Easing.Quadratic.Out)
    .onUpdate((proxy: { y: number }) => {
      trash.translation.y = proxy.y;
    })
    .chain(yDownTween)
    .start();

  this.tweens.add(yUpTween, yDownTween);
}

export const tweenHand = function (
  this: WheelOfFortuneRenderer,
  hand: { opacity: number; translation: { set: (x: number, y: number) => void } },
  time: number,
  parameters: {
    hand: {
      start: { x: number; y: number };
      mid: { x: number; y: number };
      end: { x: number; y: number };
    };
  },
) {
  hand.opacity = 1;
  const tweenMoveOut = new Tween({
    x: parameters.hand.end.x,
    y: parameters.hand.end.y,
  }).to({
    x: parameters.hand.start.x,
    y: parameters.hand.start.y
  }, 300)
    .delay(500)
    .easing(TWEEN.Easing.Linear.InOut)
    .onUpdate((proxy: { x: number; y: number }) => {
      hand.translation.set(proxy.x, proxy.y);
    });

  const tweenMoveIn = new Tween({
    x: parameters.hand.start.x,
    y: parameters.hand.start.y,
  }).to({
    x: parameters.hand.end.x,
    y: parameters.hand.end.y
  }, time)
    .easing(smoothSequenceEasingWithCutoffValue(TWEEN.Easing.Cubic.Out, TWEEN.Easing.Quadratic.Out, 0.95, 0.85, 0.05))
    .onUpdate((proxy: { x: number; y: number }) => {
      hand.translation.set(proxy.x, proxy.y);
    })
    .chain(tweenMoveOut)
    .start();

  this.tweens.add(tweenMoveIn, tweenMoveOut);
};

export const tweenHandFake = function (
  this: WheelOfFortuneRenderer,
  hand: { opacity: number; translation: { set: (x: number, y: number) => void } },
  time: number,
  parameters: {
    hand: {
      start: { x: number; y: number };
      mid: { x: number; y: number };
      end: { x: number; y: number };
    };
  },
) {
  hand.opacity = 1;
  const tweenMoveOut = new Tween({
    x: parameters.hand.mid.x,
    y: parameters.hand.mid.y,
  }).to({
    x: parameters.hand.start.x,
    y: parameters.hand.start.y
  }, 1000)
    .delay(500)
    .easing(TWEEN.Easing.Linear.InOut)
    .onUpdate((proxy: { x: number; y: number }) => {
      hand.translation.set(proxy.x, proxy.y);
    });

  const tweenMoveIn = new Tween({
    x: parameters.hand.start.x,
    y: parameters.hand.start.y,
  }).to({
    x: parameters.hand.mid.x,
    y: parameters.hand.mid.y
  }, time)
    .easing(TWEEN.Easing.Cubic.Out)
    .onUpdate((proxy: { x: number; y: number }) => {
      hand.translation.set(proxy.x, proxy.y);
    })
    .chain(tweenMoveOut)
    .start();

  this.tweens.add(tweenMoveIn, tweenMoveOut);
};

export const tweenHandReroll = function (
  this: WheelOfFortuneRenderer,
  hand: { opacity: number; translation: { set: (x: number, y: number) => void } },
  time: number
) {
  hand.opacity = 1;
  const tweenMoveIn = new Tween({
    x: 700,
    y: -50,
  }).to({
    x: -50,
    y: -800
  }, 600)
    .onUpdate((proxy: { x: number; y: number }) => {
      hand.translation.set(proxy.x, proxy.y);
    })
    .delay(time)
    .start();

  this.tweens.add(tweenMoveIn);
};
